'use client'

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react'
import { useDemo } from './demo-context'
import { ensureVisitorPersisted } from '@/hooks/use-visitor'

// ─── Context ─────────────────────────────────────────────────────────────────

interface DemoSessionContextValue {
  trackMilestone: (eventType: string, metadata?: Record<string, unknown>) => void
}

const DemoSessionContext = createContext<DemoSessionContextValue | null>(null)

// ─── Provider ────────────────────────────────────────────────────────────────

const VISITOR_COOKIE_NAME = 'iperform_visitor_id'

function getVisitorIdFromCookie(): string | null {
  if (typeof document === 'undefined') return null
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === VISITOR_COOKIE_NAME) {
      return value
    }
  }
  return null
}

export function DemoSessionProvider({ children }: { children: ReactNode }) {
  const { state } = useDemo()
  const sessionIdRef = useRef<string | null>(null)
  const startTimeRef = useRef<number>(0)
  const dedupeSetRef = useRef<Set<string>>(new Set())
  const hasInitializedRef = useRef(false)
  const prevPhaseRef = useRef(state.phase)
  const hasTrackedFirstSongRef = useRef(false)
  const prevNowPlayingIdRef = useRef<string | null>(null)

  // ─── trackMilestone ────────────────────────────────────────────────────

  const trackMilestone = useCallback(
    (eventType: string, metadata?: Record<string, unknown>) => {
      const sid = sessionIdRef.current
      if (!sid) return

      // Deduplicate coach events by markId
      if (
        (eventType === 'coach_shown' ||
          eventType === 'coach_dismissed' ||
          eventType === 'coach_skipped') &&
        metadata?.markId
      ) {
        const dedupeKey = `${eventType}:${metadata.markId}`
        if (dedupeSetRef.current.has(dedupeKey)) return
        dedupeSetRef.current.add(dedupeKey)
      }

      // Fire-and-forget POST
      fetch('/api/demo-analytics/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sid, eventType, metadata }),
      }).catch(() => {
        // swallow — analytics is best-effort
      })
    },
    []
  )

  // ─── Capture start time on mount ───────────────────────────────────────

  useEffect(() => {
    startTimeRef.current = performance.now()
  }, [])

  // ─── Session creation on mount ─────────────────────────────────────────

  useEffect(() => {
    if (hasInitializedRef.current) return
    hasInitializedRef.current = true

    async function initSession() {
      try {
        // Ensure visitor is persisted in DB
        await ensureVisitorPersisted()

        const visitorSessionId = getVisitorIdFromCookie()
        if (!visitorSessionId) return

        const res = await fetch('/api/demo-analytics/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visitorSessionId }),
        })

        if (res.ok) {
          const data = await res.json()
          if (data.sessionId) {
            sessionIdRef.current = data.sessionId
          }
        }
      } catch {
        // swallow — analytics is best-effort
      }
    }

    initSession()
  }, [])

  // ─── beforeunload beacon ───────────────────────────────────────────────

  useEffect(() => {
    const handleBeforeUnload = () => {
      const sid = sessionIdRef.current
      if (!sid) return
      const durationMs = Math.round(performance.now() - startTimeRef.current)
      navigator.sendBeacon(
        '/api/demo-analytics/close',
        JSON.stringify({ sessionId: sid, durationMs })
      )
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  // ─── Auto-track: went_live ─────────────────────────────────────────────

  useEffect(() => {
    if (state.phase === 'live' && prevPhaseRef.current === 'pre-event') {
      trackMilestone('went_live')
    }
    prevPhaseRef.current = state.phase
  }, [state.phase, trackMilestone])

  // ─── Auto-track: first_song_played + song_played ──────────────────────

  useEffect(() => {
    if (!state.nowPlaying) return
    const currentId = state.nowPlaying.id

    if (!hasTrackedFirstSongRef.current) {
      hasTrackedFirstSongRef.current = true
      trackMilestone('first_song_played', {
        songTitle: state.nowPlaying.song.title,
      })
    }

    if (currentId !== prevNowPlayingIdRef.current) {
      trackMilestone('song_played', {
        songTitle: state.nowPlaying.song.title,
      })
      prevNowPlayingIdRef.current = currentId
    }
  }, [state.nowPlaying, trackMilestone])

  return (
    <DemoSessionContext.Provider value={{ trackMilestone }}>
      {children}
    </DemoSessionContext.Provider>
  )
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Returns the session tracking context, or null if no provider is present.
 * Callers must guard: `demoSession?.trackMilestone(...)`.
 */
export function useDemoSession(): DemoSessionContextValue | null {
  return useContext(DemoSessionContext)
}
